/**
 * Validation for the transfer / exchange / loan form.
 *
 * Pure and separate from the component on purpose: this is the logic that
 * decides whether money can move, it mirrors invariants the server enforces,
 * and it is the part that actually broke — the destination currency is a
 * locked field for transfers and loans, a disabled <Select> never writes to
 * state, and reading it straight from state left the reference permanently
 * incomplete and the save button permanently dead.
 *
 * Keeping it here means it can be exercised without a browser.
 */
import type { AccountRef } from '@/lib/types';

export type MovementMode = 'transfer' | 'exchange' | 'loan';

export interface MovementInput {
  mode: MovementMode;
  from: Partial<AccountRef>;
  to: Partial<AccountRef>;
  /** Amount leaving `from`, in minor units. null while unparseable. */
  amountMinor: number | null;
  /** Exchange only: amount arriving in `to`, in minor units. */
  toAmountMinor?: number | null;
}

export interface MovementCheck {
  /**
   * The destination reference to validate and submit.
   *
   * For a transfer or a loan the currency is not the user's choice — it
   * follows the source — so it is filled in here rather than read from a form
   * field that is deliberately disabled.
   */
  toRef: Partial<AccountRef>;
  /** A rule is broken: the form is filled in, but the combination is invalid. */
  problem: string | null;
  /** Nothing is broken yet, something is simply not filled in. */
  missing: string | null;
  canSave: boolean;
}

export function isCompleteRef(ref: Partial<AccountRef>): ref is AccountRef {
  return Boolean(ref.fundId && ref.locationId && ref.currency);
}

export function checkMovement(input: MovementInput): MovementCheck {
  const { mode, from, to, amountMinor, toAmountMinor } = input;

  const toRef: Partial<AccountRef> =
    mode === 'exchange' ? to : { ...to, currency: from.currency };

  const sameCurrency = Boolean(from.currency && toRef.currency && from.currency === toRef.currency);
  const sameFund = Boolean(from.fundId && toRef.fundId && from.fundId === toRef.fundId);
  const sameAccount = sameFund && from.locationId === toRef.locationId;

  const problem = (() => {
    if (!isCompleteRef(from) || !isCompleteRef(toRef)) return null;

    if (mode === 'transfer') {
      if (!sameCurrency) return 'Различни валути — използвай „Обмяна".';
      if (sameAccount) return 'Началото и краят са една и съща сметка.';
    }
    if (mode === 'exchange') {
      if (sameCurrency) return 'Еднакви валути — използвай „Прехвърляне".';
    }
    if (mode === 'loan') {
      if (sameFund) return 'Заемът е между две различни каси.';
      if (!sameCurrency) return 'Заемът е в една валута. Обмени първо, после дай заема.';
    }
    return null;
  })();

  // A greyed-out button with no explanation is the worst state to leave a form
  // in: the user can only guess, and guessing wrong looks like a broken app.
  const missing = (() => {
    if (!from.fundId || !from.locationId) return 'Избери каса и място, откъдето излизат парите.';
    if (!from.currency) return 'Избери валута.';
    if (!toRef.fundId || !toRef.locationId) return 'Избери каса и място, където отиват парите.';
    if (amountMinor == null || amountMinor <= 0) return 'Въведи сума.';
    if (mode === 'exchange' && (toAmountMinor == null || toAmountMinor <= 0)) {
      return 'Въведи каква сума реално получаваш.';
    }
    return null;
  })();

  const canSave = problem === null && missing === null;

  return { toRef, problem, missing, canSave };
}
